import { useState } from "react";
import { typography, radius, spacing } from '../../styles/tokens';

// ── Import shared constants from planner ────────────────────
// ExternalTimeline uses its own local copies because the external view
// is public (no auth) and must not import feature-internal modules.
// Colours MUST come from tokens.js — no hardcoded hex.

const MILESTONE_TYPES = [
  { key: "start",    label: "Start",    color: "var(--khv-ms-start)",    shape: "diamond" },
  { key: "draft",    label: "Draft",    color: "var(--khv-ms-draft)",    shape: "diamond" },
  { key: "review",   label: "Review",   color: "var(--khv-ms-review)",   shape: "diamond" },
  { key: "revision", label: "Revision", color: "var(--khv-ms-revision)", shape: "diamond" },
  { key: "sign_off", label: "Sign Off", color: "var(--khv-ms-sign-off)", shape: "diamond" },
  { key: "custom",   label: "Custom",   color: "var(--khv-ms-custom)",   shape: "flag"    },
];
const MILESTONE_MAP = Object.fromEntries(MILESTONE_TYPES.map(m => [m.key, m]));

const STATUSES = [
  { key: "not_started", label: "Not Started", color: "var(--khv-status-not-started)", bg: "var(--khv-status-not-started-bg)" },
  { key: "in_progress", label: "In Progress", color: "var(--khv-status-in-progress)", bg: "var(--khv-status-in-progress-bg)" },
  { key: "complete",    label: "Complete",     color: "var(--khv-status-complete)",    bg: "var(--khv-status-complete-bg)" },
  { key: "on_hold",     label: "On Hold",      color: "var(--khv-status-on-hold)",    bg: "var(--khv-status-on-hold-bg)" },
];
export const STATUS_MAP = Object.fromEntries(STATUSES.map(s => [s.key, s]));

// ── Helpers ─────────────────────────────────────────────────
export function fmtDate(d) {
  if (!d) return "\u2014";
  return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function pct(date, minD, totalMs) {
  if (!date) return null;
  const dt = typeof date === "string" ? new Date(date + 'T00:00:00') : date;
  return Math.max(0, Math.min(100, ((dt - minD) / totalMs) * 100));
}

function ganttWindow(project) {
  const today = new Date();
  let minD = new Date(today); minD.setMonth(minD.getMonth() - 1);
  let maxD = new Date(today); maxD.setMonth(maxD.getMonth() + 4);
  if (project.start_date) { const d = new Date(project.start_date + 'T00:00:00'); if (d < minD) minD = d; }
  if (project.end_date)   { const d = new Date(project.end_date + 'T00:00:00');   if (d > maxD) maxD = d; }
  minD = new Date(minD.getFullYear(), minD.getMonth(), 1);
  maxD = new Date(maxD.getFullYear(), maxD.getMonth() + 1, 1);
  return { minD, maxD, totalMs: maxD - minD };
}

function monthLabels(minD, maxD) {
  const months = [];
  let cur = new Date(minD.getFullYear(), minD.getMonth(), 1);
  while (cur < maxD) { months.push(new Date(cur)); cur.setMonth(cur.getMonth() + 1); }
  return months;
}

// ── Shared styles ───────────────────────────────────────────
export const s = {
  page: { fontFamily: typography.fontFamily, background: "var(--khv-bg)", minHeight: "100vh", position: "relative", userSelect: "none" },
  header: { background: "var(--khv-accent)", padding: `${spacing.xl}px ${spacing.xxxl}px`, display: "flex", alignItems: "center", justifyContent: "space-between" },
  logo: { color: "var(--khv-text-inverse)", fontWeight: typography.weights.extrabold, fontSize: typography.sizes.xl, letterSpacing: "-0.02em" },
  badge: { background: "rgba(255,255,255,0.15)", color: "var(--khv-accent-light)", fontSize: typography.sizes.xs, fontWeight: typography.weights.bold, letterSpacing: "0.1em", padding: `${spacing.xs}px ${spacing.md}px`, borderRadius: 20, border: "1px solid rgba(255,255,255,0.2)" },
  content: { padding: spacing.xxxl, maxWidth: 1100, margin: "0 auto" },
  projectTitle: { fontSize: typography.sizes.xxl, fontWeight: typography.weights.extrabold, color: "var(--khv-text-primary)", marginBottom: spacing.xs },
  projectMeta: { fontSize: 11, color: "var(--khv-text-secondary)", marginBottom: spacing.xxl },
  tabBar: { display: "flex", gap: spacing.xs, borderBottom: "1px solid var(--khv-border)", marginBottom: spacing.xxl, background: "var(--khv-surface)", padding: "0 0", borderRadius: `${radius.lg}px ${radius.lg}px 0 0` },
  tab: (active) => ({ padding: `${spacing.md}px ${spacing.xl}px`, fontSize: typography.sizes.base, fontWeight: typography.weights.bold, fontFamily: typography.fontFamily, border: "none", borderBottom: active ? "2px solid var(--khv-accent)" : "2px solid transparent", background: "none", color: active ? "var(--khv-accent)" : "var(--khv-text-secondary)", cursor: "pointer" }),
  card: { background: "var(--khv-surface)", border: "1px solid var(--khv-border)", borderRadius: 10, padding: spacing.xl },
  tableHead: { fontSize: typography.sizes.sm, fontWeight: typography.weights.bold, color: "var(--khv-text-secondary)", letterSpacing: "0.06em", textTransform: "uppercase", padding: `${spacing.sm}px ${spacing.md}px`, textAlign: "left", borderBottom: "1px solid var(--khv-border)", background: "var(--khv-bg)" },
  tableCell: { padding: `${spacing.md}px ${spacing.md}px`, fontSize: typography.sizes.base, color: "var(--khv-text-primary)", borderBottom: "1px solid var(--khv-border)", verticalAlign: "middle" },
};

// ── Status badge ────────────────────────────────────────────
export function StatusBadge({ status }) {
  const st = STATUS_MAP[status] || STATUS_MAP["not_started"];
  return (
    <span style={{ display: "inline-block", padding: "2px 10px", borderRadius: 20, fontSize: typography.sizes.sm, fontWeight: typography.weights.bold, background: st.bg, color: st.color, border: `1px solid ${st.color}40` }}>
      {st.label}
    </span>
  );
}

// ── Read-only Gantt ─────────────────────────────────────────
export default function ReadOnlyGantt({ project, milestones }) {
  const [hoveredMs, setHoveredMs] = useState(null);
  const { minD, maxD, totalMs } = ganttWindow(project);
  const months = monthLabels(minD, maxD);
  const today = new Date();
  const todayPct = pct(today, minD, totalMs);
  const startPct = pct(project.start_date, minD, totalMs);
  const endPct   = pct(project.end_date,   minD, totalMs);
  const st = STATUS_MAP[project.status] || STATUS_MAP["not_started"];

  return (
    <div style={{ borderRadius: 10, overflow: "hidden", border: "1px solid var(--khv-border)", background: "var(--khv-navy)" }}>
      {/* Month header */}
      <div style={{ display: "flex", borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
        <div style={{ width: 160, flexShrink: 0, padding: `${spacing.md}px ${spacing.lg}px`, fontSize: typography.sizes.xs, fontWeight: typography.weights.bold, color: "rgba(255,255,255,0.35)", letterSpacing: "0.1em" }}>PROJECT</div>
        <div style={{ flex: 1, position: "relative", height: 36 }}>
          {months.map((m, i) => {
            const lp = pct(m, minD, totalMs);
            return (
              <div key={i} style={{ position: "absolute", left: `${lp}%`, top: 0, bottom: 0, display: "flex", alignItems: "center", paddingLeft: 6 }}>
                <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 1, background: "rgba(255,255,255,0.08)" }} />
                <span style={{ fontSize: typography.sizes.xs, fontWeight: typography.weights.bold, color: "rgba(255,255,255,0.4)", whiteSpace: "nowrap" }}>
                  {m.toLocaleDateString("en-GB", { month: "short", year: "2-digit" })}
                </span>
              </div>
            );
          })}
          {todayPct != null && <div style={{ position: "absolute", left: `${todayPct}%`, top: 0, bottom: 0, width: 1, background: "var(--khv-ms-today)", opacity: 0.6 }} />}
        </div>
      </div>

      {/* Project bar row */}
      <div style={{ display: "flex", alignItems: "center", minHeight: 56, borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <div style={{ width: 160, flexShrink: 0, padding: `0 ${spacing.lg}px` }}>
          <div style={{ fontSize: 11, fontWeight: typography.weights.bold, color: "var(--khv-text-inverse)" }}>{project.name}</div>
          <StatusBadge status={project.status} />
        </div>
        <div style={{ flex: 1, position: "relative", height: 56 }}>
          {startPct != null && endPct != null && (
            <div style={{ position: "absolute", left: `${startPct}%`, width: `${Math.max(endPct - startPct, 0.5)}%`, height: 10, top: "50%", transform: "translateY(-50%)", background: st.color, borderRadius: radius.sm, opacity: 0.85 }} />
          )}
          {todayPct != null && (
            <div style={{ position: "absolute", left: `${todayPct}%`, top: "50%", transform: "translate(-50%, 4px)" }}>
              <svg width={12} height={12} viewBox="0 0 12 12"><path d="M6 2L11 10H1L6 2Z" fill="var(--khv-ms-today)" /></svg>
            </div>
          )}
          {milestones.map((ms) => {
            const mp = pct(ms.date, minD, totalMs);
            if (mp == null) return null;
            const mt = MILESTONE_MAP[ms.type] || MILESTONE_MAP["custom"];
            return (
              <div key={ms.id} style={{ position: "absolute", left: `${mp}%`, top: "50%", transform: "translate(-50%, -180%)", zIndex: 10 }}
                onMouseEnter={() => setHoveredMs(ms)} onMouseLeave={() => setHoveredMs(null)}>
                <div style={{ position: "relative" }}>
                  {mt.shape === "flag" ? (
                    <svg width={13} height={13} viewBox="0 0 14 14" fill="none" style={{ cursor: "default" }}>
                      <path d="M2 1v12M2 1h8l-2 4 2 4H2" stroke={mt.color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill={mt.color + "33"} />
                    </svg>
                  ) : (
                    <div style={{ width: 11, height: 11, background: mt.color, transform: "rotate(45deg)", borderRadius: 2, boxShadow: `0 0 6px ${mt.color}88` }} />
                  )}
                  {hoveredMs?.id === ms.id && (
                    <div style={{ position: "absolute", bottom: "calc(100% + 8px)", left: "50%", transform: "translateX(-50%)", background: "var(--khv-tooltip-bg)", color: "var(--khv-tooltip-text)", borderRadius: radius.md, padding: `${spacing.sm}px ${spacing.md}px`, fontSize: typography.sizes.sm, fontWeight: typography.weights.semibold, whiteSpace: "nowrap", zIndex: 200, boxShadow: "0 4px 12px rgba(0,0,0,0.3)" }}>
                      <div style={{ color: mt.color, fontWeight: typography.weights.extrabold }}>{mt.label}</div>
                      {ms.label && <div style={{ color: "var(--khv-text-muted)" }}>{ms.label}</div>}
                      <div style={{ color: "var(--khv-text-muted)", marginTop: 2 }}>{fmtDate(ms.date)}</div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div style={{ padding: `${spacing.md}px ${spacing.lg}px`, borderTop: "1px solid rgba(255,255,255,0.06)", display: "flex", gap: spacing.lg, flexWrap: "wrap", alignItems: "center" }}>
        {MILESTONE_TYPES.map(mt => (
          <div key={mt.key} style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <div style={{ width: 8, height: 8, background: mt.color, transform: "rotate(45deg)", borderRadius: 1 }} />
            <span style={{ fontSize: typography.sizes.xs, color: "rgba(255,255,255,0.4)", fontWeight: typography.weights.semibold }}>{mt.label}</span>
          </div>
        ))}
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <svg width={10} height={10} viewBox="0 0 12 12"><path d="M6 2L11 10H1L6 2Z" fill="var(--khv-ms-today)" /></svg>
          <span style={{ fontSize: typography.sizes.xs, color: "rgba(255,255,255,0.4)", fontWeight: typography.weights.semibold }}>Today</span>
        </div>
      </div>
    </div>
  );
}
